/**
 * ソートパラメータ
 *
 * カラム名とソート方向を管理する
 */
export default class SortParam<T> {
    column?: T;

    direction: 'asc' | 'desc' = 'asc';

    constructor(init?: Partial<SortParam<T>>) {
        Object.assign(this, init);
    }

    public changeSort(newColumn: T): void {
        if (this.column !== newColumn) {
            this.column = newColumn;
            this.direction = 'asc';
        } else {
            if (this.direction === 'asc') {
                this.direction = 'desc';
            } else {
                this.direction = 'asc';
            }
        }
    }
}

/**
 * 文字列ソート用比較関数
 * バックエンドに合わせて、アルファベットの大文字と小文字は区別しない
 * @param {string} a 比較対象
 * @param {string} b 比較対象
 * @returns {number} 大小関係を示す数値
 */
const compareStringFunction = (a: string, b: string): number => {
    return a.localeCompare(b, 'en', { sensitivity: 'base' });
};

/**
 * その他データ型ソート用比較関数
 * @param {any} a 比較対象
 * @param {any} b 比較対象
 * @returns {number} 大小関係を示す数値
 */
const compareAnyFunction = (a: any, b: any): number => {
    return a < b ? -1 : 1;
};

/**
 * テーブルのソートパラメータを元に、Array.sortでつかう比較関数を返す
 * オブジェクトの配列を、ソートパラメータで指定したカラムと方向でソートする
 * バックエンド側のソートと同じ結果になるようにする
 * @param {SortParam<T>} sort ソートパラメータ
 * @returns {(a, b) => number} 比較
 */
export const sortParamCompareFunction = <T>(sort: SortParam<T>) => {
    return (a, b) => {
        let a_value = a[sort.column];
        let b_value = b[sort.column];

        if (a_value === b_value) {
            return 0;
        }

        // nullと文字列を比較すると意図しない結果になるため空文字列にする
        if (typeof a_value === 'string' || typeof b_value === 'string') {
            if (a_value == null) {
                a_value = '';
            }
            if (b_value == null) {
                b_value = '';
            }
        }

        const compare =
            typeof a_value === 'string' && typeof b_value === 'string' ? compareStringFunction : compareAnyFunction;

        if (sort.direction === 'asc') {
            return compare(a_value, b_value);
        } else {
            return compare(b_value, a_value);
        }
    };
};

/**
 * テーブルのソートパラメータとページングパラメータを元に、データ全体からソート済み表示対象の部分配列を抽出する
 * @param {Array<T>} source データ全体
 * @param {SortParam<U>} sort ソートパラメータ
 * @param {{currentPage: number; perPage: number}} paging ページングパラメータ
 * @returns {Array<T>} ソート済み表示対象
 */
export const sortAndPaging = <T, U>(
    source: Array<T>,
    sort: SortParam<U>,
    paging: { currentPage: number; perPage: number }
): Array<T> => {
    const startIndex = (paging.currentPage - 1) * paging.perPage;
    const endIndex = startIndex + paging.perPage;

    const copy = source.slice();
    copy.sort(sortParamCompareFunction(sort));
    return copy.slice(startIndex, endIndex);
};
